<template>
  <article
    class="release-highlight flex-column"
    :class="{
      'release-highlight_slide': isSlide,
    }"
    data-qa="release-highlight"
  >
    <div class="release-highlight__media-frame">
      <img
        v-if="highlight.media.type === 'image'"
        class="release-highlight__media"
        :src="`${mediaFolderUrl}/${highlight.media.fileName}`"
        :alt="highlight.headline"
        loading="lazy"
      >
      <video
        v-else-if="highlight.media.type === 'video'"
        class="release-highlight__media"
        :src="`${mediaFolderUrl}/${highlight.media.fileName}`"
        :poster="`${mediaFolderUrl}/${highlight.media.posterFileName}`"
        :aria-label="highlight.headline"
        controls
        preload="none"
      />
      <ReleaseHighlightThemedSvg
        v-else-if="highlight.media.type === 'themed-svg'"
        class="release-highlight__media"
        :url="`${mediaFolderUrl}/${highlight.media.fileName}`"
        :label="highlight.headline"
      />
    </div>
    <div class="release-highlight__text flex-column-union">
      <h3 class="release-highlight__headline">
        {{ highlight.headline }}
        <span
          v-if="version"
          class="release-highlight__version color-text-secondary"
        >{{ version }}</span>
      </h3>
      <ReleaseMarkdown :markdown="highlight.description" />
    </div>
  </article>
</template>

<script setup lang="ts">
import ReleaseHighlightThemedSvg from './release-highlight-themed-svg.vue';
import type {
  TReleaseHighlight,
} from './release-highlights.ts';
import ReleaseMarkdown from './release-markdown.vue';

defineProps<{
  highlight: TReleaseHighlight,
  /** URL path of the folder with the media of the release, e.g. `/release-highlights/2.14.0`. */
  mediaFolderUrl: string,
  /**
   * Shows the highlight as one slide of several, e.g. in a modal that switches between them: the
   * media takes a fixed 16:9 frame and the text a readable width below it.
   */
  isSlide?: boolean,
  /** Shown after the headline, where highlights of several releases are shown without headings. */
  version?: string,
}>();
</script>

<style scoped lang="scss">
.release-highlight {
  &__headline {
    font-size: 1.1rem;
    font-weight: bold;
  }

  &__version {
    margin-left: var(--gap-union);
    font-weight: normal;
  }

  &__media {
    display: block;
    width: 100%;
    height: auto;
  }

  // Every slide takes the same height, whatever the size of its image or video, so that a modal
  // does not change its height and the headline below stays in place between slides. The media is
  // scaled to fit without being cropped and centered in the frame.
  &_slide &__media-frame {
    position: relative;
    aspect-ratio: 16 / 9;
  }

  // Positioned, so that the media cannot stretch the frame: a box with an aspect ratio still grows
  // to the height of its content, e.g. of an image that is narrower than 16:9.
  &_slide &__media {
    position: absolute;
    inset: 0;
    height: 100%;
    object-fit: contain;
  }

  /*
    On a slide, the image takes the full width of a wide modal, which makes for lines of text
    that are too long to read comfortably: the eye loses its place when jumping back to the start
    of the next line. The headline and description are therefore limited to a common line length.

    Recommended line lengths:
    - 45 to 75 characters, 66 being ideal: Robert Bringhurst, The Elements of Typographic Style.
    - 50 to 75 characters: Baymard Institute, usability research on readability.
    - At most 80 characters: WCAG 2.1, success criterion 1.4.8 Visual Presentation (AAA),
      https://www.w3.org/WAI/WCAG21/Understanding/visual-presentation.html

    `ch` is the width of the digit 0, which is wider than most letters, so 65ch hold about 70 to 75
    characters of running text.
  */
  &_slide &__text {
    max-width: 65ch;
  }
}
</style>
